import React, { useEffect, useState } from "react"
import { Link, useSearchParams } from "react-router-dom"
import JobShareButton from "../components/JobShareButton"
import { getAPIBaseURL } from "../services/helpers"
import { deleteData, getData } from "../services/request"
import { employmentTypeLabel, jobStatusColor, jobStatusLabel } from "../utils/employmeLabels"

const EDITABLE_STATUSES = ["DRAFT", "PENDING_PAYMENT", "PENDING_APPROVAL", "REJECTED", "LIVE"]
const DELETABLE_STATUSES = ["DRAFT", "PENDING_PAYMENT", "REJECTED"]
const CLOSABLE_STATUSES = ["LIVE", "PENDING_APPROVAL"]

const canEditJob = (status) => EDITABLE_STATUSES.includes(status)
const canDeleteJob = (status) => DELETABLE_STATUSES.includes(status) || CLOSABLE_STATUSES.includes(status)
const getDeleteLabel = (status) =>
  CLOSABLE_STATUSES.includes(status) ? "Close job" : "Delete"

const statusFilters = [
  { key: "ALL", label: "All" },
  { key: "DRAFT", label: "Draft" },
  { key: "PENDING_PAYMENT", label: "Pending payment" },
  { key: "PENDING_APPROVAL", label: "Pending approval" },
  { key: "LIVE", label: "Live" },
  { key: "CLOSED", label: "Closed" },
  { key: "EXPIRED", label: "Expired" },
]

const EmployerJobsList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [status, setStatus] = useState(searchParams.get("status") || "ALL")
  const [deletingId, setDeletingId] = useState(null)

  const fetchJobs = async () => {
    setLoading(true)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/jobs`
    const headers = {}
    const params = status && status !== "ALL" ? { status } : {}
    const res = await getData(url, headers, params)
    setLoading(false)
    if (res.error === "") {
      if (res.result.status === 200) {
        setJobs(res.result.data?.data || [])
      } else {
        setError(res.result.data?.message || "Could not load jobs")
      }
    } else {
      setError(res.result?.data?.message || "Network error")
    }
  }

  useEffect(() => {
    fetchJobs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status])

  const formatDate = (iso) => {
    try {
      return new Date(iso).toLocaleString()
    } catch (e) {
      return iso
    }
  }

  const setFilter = (value) => {
    setStatus(value)
    const next = new URLSearchParams(searchParams)
    if (value && value !== "ALL") {
      next.set("status", value)
    } else {
      next.delete("status")
    }
    setSearchParams(next)
  }

  const handleDeleteJob = async (job) => {
    const label = getDeleteLabel(job.status)
    const message =
      job.status === "LIVE" || job.status === "PENDING_APPROVAL"
        ? "Close this job? It will no longer accept new applicants."
        : "Delete this job permanently? This cannot be undone."
    if (!window.confirm(message)) return

    setDeletingId(job._id)
    setError("")
    const url = `${getAPIBaseURL()}/employme/employer/jobs/${job._id}`
    const res = await deleteData(url, {}, {})
    setDeletingId(null)
    if (res.error === "" && res.result?.status === 200) {
      await fetchJobs()
    } else {
      setError(res.result?.data?.data || res.result?.data?.message || `Could not ${label.toLowerCase()} job`)
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-3 sm:px-0">
      <div className="mb-4 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 sm:text-xl">Job posts</h2>
          <p className="text-xs text-gray-500 sm:text-sm">
            Manage your job ads, statuses, and applicants.
          </p>
        </div>
        <div className="grid w-full gap-2 sm:w-auto sm:grid-cols-[minmax(180px,1fr)_auto] sm:items-center">
          <div className="flex items-center gap-2">
            <span className="shrink-0 whitespace-nowrap text-xs font-medium text-gray-600">Status</span>
            <select
              value={status}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs text-gray-700 focus:border-black focus:outline-none focus:ring-1 focus:ring-black sm:text-sm"
            >
              {statusFilters.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label}
                </option>
              ))}
            </select>
          </div>
          <Link
            to="/employer/jobs/new"
            className="inline-flex items-center justify-center rounded-lg bg-black px-4 py-2 text-xs font-semibold text-white hover:bg-gray-900 sm:text-sm"
          >
            New job
          </Link>
        </div>
      </div>
      <div className="rounded-lg bg-white shadow-sm border border-gray-200">
        {loading ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500">Loading...</div>
        ) : error ? (
          <div className="p-4 sm:p-6 text-sm text-red-600">{error}</div>
        ) : jobs.length === 0 ? (
          <div className="p-4 sm:p-6 text-sm text-gray-500">
            No jobs found. Create your first job to start receiving applicants.
          </div>
        ) : (
          <ul className="divide-y divide-gray-200">
            {jobs.map((job) => (
              <li key={job._id} className="p-4 sm:p-5">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className="font-medium text-sm text-gray-900">
                        {job.title || "Untitled role"}
                      </span>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold ${jobStatusColor(job.status)}`}
                      >
                        {jobStatusLabel(job.status)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      {(
                        typeof job.location === "string"
                          ? job.location
                          : [job.location?.city, job.location?.state, job.location?.country]
                              .filter(Boolean)
                              .join(", ")
                      ) || "Location not set"}{" "}
                      •{" "}
                      {employmentTypeLabel(job.employmentType || job.type)}
                    </p>
                    <p className="mt-1 text-xs text-gray-400">
                      Created: {formatDate(job.createdAt)}
                    </p>
                    {job.applicantsCount != null ? (
                      <p className="mt-1 text-xs text-gray-500">
                        {job.applicantsCount} applicants
                      </p>
                    ) : null}
                  </div>
                  <div className="flex w-full flex-wrap gap-2 sm:w-auto sm:flex-nowrap sm:shrink-0">
                    <Link
                      to={`/employer/jobs/${job._id}`}
                      className="w-full sm:w-auto rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 text-center"
                    >
                      Open
                    </Link>
                    {job.status === "LIVE" ? (
                      <JobShareButton
                        jobId={job._id}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-1 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
                      />
                    ) : null}
                    {canEditJob(job.status) ? (
                      <Link
                        to={`/employer/jobs/${job._id}/edit`}
                        className="w-full sm:w-auto rounded-lg border border-gray-200 px-3 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50 text-center"
                      >
                        Edit
                      </Link>
                    ) : null}
                    {canDeleteJob(job.status) ? (
                      <button
                        type="button"
                        onClick={() => handleDeleteJob(job)}
                        disabled={deletingId === job._id}
                        className="w-full whitespace-nowrap sm:w-auto rounded-lg border border-red-200 px-3 py-2 text-xs font-medium text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {deletingId === job._id ? "..." : getDeleteLabel(job.status)}
                      </button>
                    ) : null}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default EmployerJobsList

